import { ILookupModel, IMultiSelectOption } from '../../../../interfaces/ILookup';
import { Stack, Table } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import { ApplicationHelpType } from '../../../../utilities/Constants';
import CheckmateNSelect from '../../../shared/CheckmateNSelect';
import { Help } from '../../../shared/Help';
import { ISameCaseMatchedCasesSearchModel } from '../../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import { LoadingButton } from '../../../../shared/buttons/LoadingButton';
import { statusOptions } from '../../same-cases-admin/manage/SameCaseManageSearchFilters';
import { useGetJurisdictions } from '../../../../shared/react-query-hooks/useGetJurisdictions';
import { useGetUsStatesLookup } from '../../../../shared/react-query-hooks/useUsStatesLookup';

const lookupToOptionMapper = (lookupItem: ILookupModel) =>
    ({
        label: lookupItem.displayName!,
        value: lookupItem.name!,
        id: lookupItem.id,
        guid: lookupItem.guid,
    }) as IMultiSelectOption;

const lookupToStateOptionMapper = (lookupItem: ILookupModel) =>
    ({
        id: lookupItem.id,
        label: `${lookupItem.displayName} - ${lookupItem.name}`,
        value: lookupItem.name ?? '',
        guid: lookupItem.guid ?? '',
    }) as IMultiSelectOption;

export interface ISameCaseSearchFiltersProps {
    currentFilters: ISameCaseMatchedCasesSearchModel;
    isSearching?: boolean;
    onApplyFilters: (filters: ISameCaseMatchedCasesSearchModel) => void;
}

export function ViewSameCasesSearchFilters(props: ISameCaseSearchFiltersProps) {
    const [localFilters, setLocalFilters] = useState<ISameCaseMatchedCasesSearchModel>(
        props.currentFilters
    );

    const jurisdictionsLookup = useGetJurisdictions(props.currentFilters.caseFileModelId);
    const usStatesLookup = useGetUsStatesLookup();

    const selectedStatusOptions =
        localFilters.statuses
            ?.map((id) => statusOptions.find((lu) => lu.id === id))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const availableStateOptions = usStatesLookup.data?.map(
        lookupToStateOptionMapper
    ) as IMultiSelectOption[];

    const selectedStateOptions =
        localFilters.states
            ?.map((stateId) => availableStateOptions.find((lu) => lu.guid === stateId))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const availableJurisdictionOptions =
        jurisdictionsLookup.data
            ?.filter((x) => {
                if (selectedStateOptions?.length === 0) return true;
                return selectedStateOptions
                    .flatMap((state) => state.value)
                    .includes(x.displayName?.slice(-2) ?? '');
            })
            .map(lookupToOptionMapper) ?? [];

    const selectedJurisdictionOptions =
        localFilters.jurisdictions
            ?.map((jurisdictionId) =>
                availableJurisdictionOptions.find((lu) => lu.guid === jurisdictionId)
            )
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const handleApplyFiltersClick = () => {
        props.onApplyFilters(localFilters);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleApplyFiltersClick();
        }
    };

    const handleCaseNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, caseNumber: e.target.value }));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, name: e.target.value }));
    };

    const handleJurisdictionsChange = (selectedItems: IMultiSelectOption[]) => {
        const jurisdictionGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, jurisdictions: jurisdictionGuids }));
    };

    const handleStatesChange = (selectedItems: IMultiSelectOption[]) => {
        const stateGuids = selectedItems?.map((x) => x.guid!);
        const foundStateLookupItems = selectedItems.map((item) =>
            (usStatesLookup.data ?? []).find((lu) => lu.guid === item.guid)
        );
        const foundStateAbbreviations = foundStateLookupItems.map((item) => item?.name);

        // for each state, if a jurisdiction exists that doesn't belong to a selected state, remove i
        const filteredJurisdictions = localFilters.jurisdictions?.filter((j) => {
            const found = jurisdictionsLookup.data?.find((lu) => lu.guid === j);
            if (found) {
                return foundStateAbbreviations.includes(found.name?.slice(-2));
            }

            return false;
        });

        setLocalFilters((prev) => ({
            ...prev,
            states: stateGuids,
            jurisdictions: filteredJurisdictions,
        }));
    };

    const handleStatusChange = (selectedItems: IMultiSelectOption[]) => {
        const statusIds = selectedItems?.map((x) => x.id!);
        setLocalFilters((prev) => ({ ...prev, statuses: statusIds }));
    };

    const handleClearFiltersClick = () => {
        setLocalFilters({
            ...localFilters,
            caseNumber: undefined,
            jurisdictions: undefined,
            name: undefined,
            states: undefined,
            statuses: undefined,
            pagedModel: { ...localFilters.pagedModel, pageNumber: 1 },
            sortModel: {},
        } as ISameCaseMatchedCasesSearchModel);
    };

    useEffect(() => {
        setLocalFilters(props.currentFilters);
    }, [props.currentFilters]);

    const statusHelpText =
        'Open: A Same Case Group where atleast one of the cases are Open-Active, Alt-Monitored, Tendered-Pending or Named Not Served. \n\n Closed: A Same Case Group where all of the cases are Resolved, Alt-Closed or Tendered-Accepted. \n\n Inactive: A Same Case Group where atleast one of the cases are Inactive and none of the cases are Open-Active, Alt-Monitored, Tendered-Pending or Named Not Served.';

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">
                                Status
                                <Help
                                    helpText={statusHelpText}
                                    title="Status"
                                    type={ApplicationHelpType.Info}
                                />
                            </td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleStatusChange}
                                    options={statusOptions}
                                    value={selectedStatusOptions}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">State</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleStatesChange}
                                    options={availableStateOptions}
                                    value={selectedStateOptions}
                                    disabled={usStatesLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Jurisdiction</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleJurisdictionsChange}
                                    options={availableJurisdictionOptions}
                                    value={selectedJurisdictionOptions}
                                    disabled={jurisdictionsLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Case Number</td>
                            <td>
                                <input
                                    className="form-control full-width"
                                    type="text"
                                    value={localFilters.caseNumber ?? ''}
                                    onChange={handleCaseNumberChange}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Case Name</td>
                            <td>
                                <input
                                    className="form-control full-width"
                                    type="text"
                                    value={localFilters.name ?? ''}
                                    onKeyDown={handleKeyDown}
                                    onChange={handleNameChange}
                                />
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />
                <button
                    className="btn btn-default"
                    onClick={handleClearFiltersClick}
                    disabled={props.isSearching}
                >
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
}
